import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchStats } from '../store/slice/stats-slice';
import { fetchCashRegisterData } from '../store/slice/cash-register-slice';
import { StatsFetchType } from '../enum/StatsFetchType';
import { CASH_REGISTER_URL } from '../util/url';
import { buildRequestForDeepestGeoLevel } from '../util/buildRequestForDeepestGeoLevel';
import { GeoClass } from '../interface/response/GeoFiltersResponse';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import useDateAndTime from './useDateAndTime';
import { StatsGroupEnum } from '../enum/StatsGroupEnum';
import { StatsDataRequest } from '../interface/request/StatsDataRequest';
import { isGeoColumn } from '../util/table-helper';
import { resolveGroupByFromColumns } from '../util/resolveGroupBy';
import { resolveGroupBy } from '../util/group-by-helper';
import { setSelectedGroupBy } from '../store/slice/stats-slice';
import { commitPendingVisible } from '../store/slice/config/config';
import { persistColumnConfig } from '../util/persist-column-config';
import { fetchGeoFilters } from '../store/slice/filter-slice';
import store from '../store/store';

/**
 * useAllFilters
 * ----------------------------------------------------
 * Single entry point for "apply all filters and fetch".
 * Every action that should apply the current filter state
 * calls handleApplyAllFilters, so they all run the EXACT
 * same logic and always pick up the complete current state
 * (geo columns, date range, device/payment filters, etc.).
 */
export const useAllFilters = () => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.globalFilters);
  const { pathname } = useLocation();
  const { startDate, endDate } = useDateAndTime();
  const selectedCurrency = useAppSelector((state) => state.configSlice.selectedCurrency);
  const pageKey = useAppSelector((state) => state.configSlice.activePageKey);
  const shouldSum = useAppSelector((state) => state.globalFilters.shouldSumByDevice);
  const currentGeoGroup = useAppSelector((state) => state.stats.groupBy);
  const geoFiltersDataState = useAppSelector((state) => state.filters.geoFiltersData);
  const columnsState = useAppSelector((state) => state.configSlice.columnsByPage[pageKey]);
  const geoFiltersData = useMemo(() => geoFiltersDataState ?? {}, [geoFiltersDataState]);
  const columnsForPage = useMemo(() => columnsState ?? [], [columnsState]);
  const selectedDevices = useMemo(() => filters.PaymentSource ?? [], [filters.PaymentSource]);
  const selectedPaymentTypes = useMemo(() => filters.PaymentType ?? [], [filters.PaymentType]);
  const lastFetchedStartDate = useAppSelector((state) => state.filters.lastFetchedStartDate);

  const fetchStatsWithViewPerCurrency = useCallback(
    (payload: StatsDataRequest) => {
      dispatch(
        fetchStats({
          type: StatsFetchType.STATS,
          request: { ...payload },
        }),
      );

      /**
       *   Skip the second (view per currency) fetch if the user already
       * grouped by CCY - would be a pointless duplicate otherwise. */
      if (payload.groupBy?.[0] !== StatsGroupEnum.CCY) {
        dispatch(
          fetchStats({
            type: StatsFetchType.VIEW_PER_CURRENCY,
            request: {
              ...payload,
              groupBy: [StatsGroupEnum.CCY],
            },
          }),
        );
      }
    },
    [dispatch],
  );

  const handleApplyAllFilters = useCallback(async () => {
    /**
     * Compute the geo level FRESH, from the current (pending) column
     * visibility. This is the "geoGroup" input for resolveGroupBy -
     * a single number, not the final array. */
    const geoColumnsOrdered = Object.keys(geoFiltersData).filter(isGeoColumn);
    const geoGroupFromColumns = resolveGroupByFromColumns(geoColumnsOrdered, columnsForPage);
    const geoGroup = geoGroupFromColumns ?? currentGeoGroup; // fallback if this page has no geo columns

    /**
     * Commit pending visibility (checkbox choices made in the Sum By
     * dropdown become the actual column visibility in the table). */
    dispatch(commitPendingVisible({ pageKey }));

    /**
     * Sync state.stats.groupBy (the geo part) - so every other part
     * of the UI reading that value is consistent from the next render on. */
    if (geoGroupFromColumns !== undefined) {
      dispatch(setSelectedGroupBy(geoGroupFromColumns));
    }

    /**
     * The final groupBy (geo + device + paymentType) is computed HERE,
     * via a direct resolveGroupBy call - NOT through the
     * useResolvedTableState hook, since that hook would read the
     * stale state.stats.groupBy captured at the start of this render. */
    const { groupBy: finalGroupBy } = resolveGroupBy({
      geoGroup,
      selectedDevices,
      selectedPaymentTypes,
      shouldSum,
    });

    const dateChanged = (() => {
      if (lastFetchedStartDate === undefined) return true;
      if (lastFetchedStartDate === null || startDate === null) return lastFetchedStartDate !== startDate;
      return new Date(lastFetchedStartDate).getTime() !== new Date(startDate).getTime();
    })();

    let effectiveFilters = filters;

    if (dateChanged) {
      await dispatch(
        fetchGeoFilters({
          ClientID: filters.Client?.length ? filters.Client.map((c) => c.ID) : null,
          CompanyID: filters.Company?.length ? filters.Company.map((c) => c.ID) : null,
          RegionID: filters.Region?.length ? filters.Region.map((c) => c.ID) : null,
          GroupID: filters.Group?.length ? filters.Group.map((c) => c.ID) : null,
          GetAllAbove: false,
          GetAllBelow: false,
          GetUserLevel: false,
          StartDate: startDate,
        }),
      ).unwrap();

      // The auto-select logic currently lives in a React effect (it runs
      // on the next render cycle of another component), so we give React
      // a chance to run it before continuing - otherwise the payload
      // would go out with the stale filters.
      await new Promise((resolve) => setTimeout(resolve, 0));

      // Now read the fresh state directly from the store
      effectiveFilters = store.getState().globalFilters;
    }

    const payload = {
      Date: { StartDate: startDate, EndDate: endDate },
      ...buildRequestForDeepestGeoLevel(effectiveFilters),
      games: effectiveFilters.Games?.length ? effectiveFilters.Games.map((g) => Number(g.ID)) : null,
      paymentSources: selectedDevices.length ? selectedDevices.map((p) => p.ID) : null,
      paymentType: selectedPaymentTypes.length ? selectedPaymentTypes.map((type) => type.ID) : null,
      statisticBy: effectiveFilters.GameActivity?.id ?? null,
      hasTestData: effectiveFilters.TestData?.id ?? null,
      CCY: selectedCurrency ?? null,
      groupBy: finalGroupBy,
    };

    /**
     * Cash register page has a completely different request shape/endpoint -
     * bail out early, before the persist/fetch-stats logic that only applies
     * to the statistics page. */
    if (pathname === CASH_REGISTER_URL) {
      dispatch(
        fetchCashRegisterData({
          GroupID: effectiveFilters.Group?.[0]?.ID ?? null,
          LocationID: effectiveFilters.Location?.length ? effectiveFilters.Location.map((l: GeoClass) => l.ID) : null,
          From: startDate,
          To: endDate,
          PageNumber: 1,
          PageSize: 20,
        }),
      );
      return;
    }

    /**
     * Persist happens AFTER commitPendingVisible - the redux
     * store already holds the committed visibility at this point,
     * since dispatch is synchronous for the store update itself.  */
    persistColumnConfig(dispatch);

    fetchStatsWithViewPerCurrency(payload);
  }, [
    geoFiltersData,
    columnsForPage,
    currentGeoGroup,
    pageKey,
    selectedDevices,
    selectedPaymentTypes,
    shouldSum,
    filters,
    startDate,
    endDate,
    selectedCurrency,
    pathname,
    dispatch,
    fetchStatsWithViewPerCurrency,
  ]);

  return { handleApplyAllFilters };
};
